import Banner from "./banner";
import FeedCard from "./feed/feed-card";
import SearchBar from "./search-bar-";
import CreateFeed from "./feed/create-feed";
import { useAppDispatch, useAppSelector } from "../../../hooks/store-hooks";
import { MODAL_NAME } from "../../../constants";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetHomeDataQuery } from "../../../store/api/homeApi";
import { BtnLoader } from "../../../icons";
import { addPostData, clearHomeData } from "../../../store/slices/homeSlice";
import DeletePost from "./feed/delete-feed";
import ReportFeed from "./feed/report-feed";
import StartFollowing from "./feed/empty-feed";
import DeleteReaction from "./feed/delete-comment";
import ReportReaction from "./feed/report-comment";

export const preComputeFollowData = (posts: any) => {
  return posts?.reduce((acc: any, post: any) => {
    acc[post?.user?.id] = post?.user?.is_follows;
    return acc;
  }, {});
};

const MainHomeContent = ({
  topicsQueryString,
}: {
  topicsQueryString?: string;
}) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const { homeData } = useAppSelector((state: any) => state.home ?? {});
  const { modalName, isModalOpen } = useAppSelector(
    (state: any) => state.modal ?? {},
  );
  const [selectedFeedType, setSelectedFeedType] = useState("top");

  const [trigger, { isFetching }] = useLazyGetHomeDataQuery();

  const posts = homeData?.posts ?? [];
  const isEnd = homeData?.is_end;

  const memoFollowData = useMemo(() => preComputeFollowData(posts), [posts]);
  const [isFollow, setIsFollow] = useState<{ [key: string]: boolean }>({});
  const dispatch = useAppDispatch();
  const [deletedCommentInfo, setDeletedCommentInfo] = useState<{
    postId: string;
    commentId: string;
    reactionType?: string;
  } | null>(null);

  const handleCommentDeleted = (
    postId: string,
    commentId: string,
    reactionType: string,
  ) => {
    setDeletedCommentInfo({ postId, commentId, reactionType });
  };
  useEffect(() => {
    trigger({ feedOffset: 0, feedType: selectedFeedType }, false);
  }, []);

  useEffect(() => {
    setIsFollow((prevIsFollow) => {
      const newIsFollow = preComputeFollowData(posts);
      if (JSON.stringify(newIsFollow) !== JSON.stringify(prevIsFollow)) {
        return newIsFollow;
      }
      return prevIsFollow;
    });
  }, [memoFollowData]);

  useEffect(() => {
    handleFeedTypeChange({ tag: "top" });
  }, [topicsQueryString]);

  const FeedType = [
    { id: 1, tag: "top", name: "Top" },
    { id: 2, tag: "recent", name: "Recent" },
  ];

  const getNewPostData = async () => {
    try {
      const response = await trigger(
        {
          feedOffset: posts.length,
          feedType: selectedFeedType,
          topics: topicsQueryString,
        },
        false,
      )?.unwrap();
      if (response?.success) {
        dispatch(addPostData(response.data));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleFeedTypeChange = async ({ tag }: { tag: string }) => {
    setSelectedFeedType(tag);
    try {
      const response = await trigger(
        {
          feedOffset: 0,
          feedType: tag,
          topics: topicsQueryString,
        },
        false,
      )?.unwrap();
      if (response?.success) {
        dispatch(clearHomeData());
        dispatch(addPostData(response.data));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const lastPostElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          getNewPostData();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetching],
  );

  useEffect(() => {
    dispatch(clearHomeData());
  }, [selectedFeedType]);

  return (
    <div className="lg:col-span-6 ">
      <Banner />
      <SearchBar />
      <div className="flex">
        {FeedType.map((feed: any) => (
          <div
            key={feed.id}
            className={`flex items-center justify-center px-[16px] py-[7px] rounded-[40px] cursor-pointer ${
              selectedFeedType === feed.tag
                ? "bg-[#007AFF] text-white"
                : "border border-[#E1E1E1] text-[#404040]"
            } font-[590]  text-[17px] ${feed.id !== 1 && "ml-[12px]"}`}
            onClick={() =>
              handleFeedTypeChange({
                tag: feed.tag,
              })
            }
          >
            {feed.name}
          </div>
        ))}
      </div>
      <div className="space-y-4 mt-[16px] pb-6">
        {posts.length > 0
          ? posts.map((post: any, index: number) => (
              <FeedCard
                key={index}
                postData={post}
                follow={isFollow[post.user?.id]}
                setIsFollow={(followStatus: boolean) =>
                  setIsFollow((prev) => ({
                    ...prev,
                    [post.user?.id]: followStatus,
                  }))
                }
                selectedFeedType={selectedFeedType}
                deletedCommentInfo={deletedCommentInfo}
              />
            ))
          : !isFetching && <StartFollowing />}
      </div>
      {!isEnd ? (
        <div
          className="w-full flex justify-center my-4"
          ref={lastPostElementRef}
        >
          {" "}
          <BtnLoader />
        </div>
      ) : null}
      {modalName === MODAL_NAME.CREATE_POST && isModalOpen && <CreateFeed />}
      {modalName === MODAL_NAME.DELETE_POST ? <DeletePost /> : <></>}
      {modalName === MODAL_NAME.REPORT ? <ReportFeed /> : <></>}
      {modalName === MODAL_NAME.DELETE_REACTION ? (
        <DeleteReaction onCommentDeleted={handleCommentDeleted} />
      ) : (
        <></>
      )}
      {modalName === MODAL_NAME.REPORT_REACTION ? <ReportReaction /> : <></>}
    </div>
  );
};

export default MainHomeContent;
